import { fetchAuthSession, fetchUserAttributes, updateUserAttributes, signOut } from 'aws-amplify/auth';

// Migration: move flatmate_tokens → movetwo_tokens
try {
  const legacy = localStorage.getItem('flatmate_tokens');
  if (legacy) {
    localStorage.setItem('movetwo_tokens', legacy);
    localStorage.removeItem('flatmate_tokens');
  }
} catch {
  // Not in a browser environment
}

const LEGACY_TOKEN_KEY = 'movetwo_tokens';

/**
 * Handle SSO token handoff from the Chrome extension.
 *
 * The extension appends tokens as a URL hash fragment:
 *   `#auth=BASE64_JSON`
 * where the JSON contains { idToken, accessToken, refreshToken }.
 *
 * This function decodes the tokens and stores them in localStorage using
 * Amplify v6's expected key format so `fetchAuthSession()` finds them.
 *
 * Must be called BEFORE Amplify.configure() so the Authenticator component
 * detects the existing session and skips the login screen.
 *
 * Any additional hash params (e.g. `&property=...`) are preserved.
 */
export function handleSsoTokenHandoff(clientId: string): void {
  const hash = window.location.hash;
  if (!hash.includes('auth=')) return;

  try {
    // Parse hash params — format: #auth=BASE64&property=xyz&pricing
    const hashContent = hash.slice(1); // remove leading #
    const params = new URLSearchParams(hashContent);
    const authPayload = params.get('auth');

    if (!authPayload) return;

    const tokens = JSON.parse(atob(authPayload)) as {
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };

    if (!tokens.accessToken || !tokens.idToken) return;

    // Decode the access token to extract the `sub` claim (used as username by Amplify)
    const payloadPart = tokens.accessToken.split('.')[1];
    const claims = JSON.parse(atob(payloadPart)) as { sub?: string; iat?: number };
    const username = claims.sub;
    if (!username) return;

    // Calculate clock drift (difference between token iat and local time)
    const iatMs = (claims.iat ?? 0) * 1000;
    const clockDrift = iatMs > 0 ? iatMs - Date.now() : 0;

    // Store tokens in Amplify v6 localStorage format
    const prefix = 'CognitoIdentityServiceProvider';
    const keyBase = `${prefix}.${clientId}.${username}`;

    localStorage.setItem(`${prefix}.${clientId}.LastAuthUser`, username);
    localStorage.setItem(`${keyBase}.accessToken`, tokens.accessToken);
    localStorage.setItem(`${keyBase}.idToken`, tokens.idToken);
    if (tokens.refreshToken) {
      localStorage.setItem(`${keyBase}.refreshToken`, tokens.refreshToken);
    }
    localStorage.setItem(`${keyBase}.clockDrift`, `${clockDrift}`);

    // Remove the auth param from the hash, preserve any other params
    params.delete('auth');
    const remaining = params.toString();
    if (remaining) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${remaining}`);
    } else {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  } catch (err) {
    console.warn('SSO token handoff failed, falling back to login:', err);
  }
}

/**
 * Check if the user has an active Amplify session.
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens?.idToken;
  } catch {
    return false;
  }
}

/**
 * Get a valid ID token string. Amplify handles refresh automatically.
 */
export async function getValidToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Get the user's display name from Cognito user attributes, or null if not set.
 * Falls back to the name claim in the ID token if the attributes API fails
 * (e.g. revoked access token).
 */
export async function getUserName(): Promise<string | null> {
  try {
    const attrs = await fetchUserAttributes();
    const name = attrs.name;
    if (name && name.trim().length > 0) return name.trim();
  } catch {
    // fetchUserAttributes failed — fall back to ID token claims
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
      const name = payload?.['name'] as string | undefined;
      if (name && name.trim().length > 0) return name.trim();
    } catch {
      // Both approaches failed
    }
  }
  return null;
}

/**
 * Update the user's display name in Cognito.
 */
export async function updateUserName(name: string): Promise<void> {
  await updateUserAttributes({
    userAttributes: { name: name.trim() },
  });
}

/**
 * Sign out the user. Also cleans up legacy token storage from the old auth flow.
 */
/**
 * Get the user's email from Cognito user attributes.
 */
export async function getUserEmail(): Promise<string | null> {
  try {
    const attrs = await fetchUserAttributes();
    return attrs.email ?? null;
  } catch {
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
      return (payload?.['email'] as string) ?? null;
    } catch {
      return null;
    }
  }
}

/**
 * Get the user's Cognito sub (user ID) from the session.
 */
export async function getUserId(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
    return (payload?.['sub'] as string) ?? null;
  } catch {
    return null;
  }
}

/**
 * Returns the Cognito Hosted UI forgot-password URL.
 */
export function getChangePasswordUrl(): string {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN;
  if (!domain) {
    console.warn('VITE_COGNITO_DOMAIN is not set — change password link may not work');
    return '';
  }
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const redirectUri = encodeURIComponent(import.meta.env.VITE_WEB_URL);
  return `https://${domain}/forgotPassword?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`;
}

export async function logout(): Promise<void> {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  await signOut();
}

/**
 * Clean up legacy tokens from the old OAuth flow (migration helper).
 * Called once on app load to clear stale data.
 */
export function clearLegacyTokens(): void {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

/**
 * Kept for backward compatibility with api.ts — signals to the API layer
 * that tokens should be considered invalid. With Amplify, this is a no-op
 * since Amplify manages its own token storage.
 */
export function clearTokens(): void {
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}
